// src/views/hrms/offices/OfficeEditPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getOffice,
  updateOffice,
  getShiftsByOffice,
  createShift,
  updateShift,
  deleteShift,
} from "services/hrms.api";

import {
  ChevronLeft,
  LocateFixed,
  MapPin,
  Clock,
  Plus,
  Trash2,
  Save,
} from "lucide-react";

import { CountrySelect, StateSelect, CitySelect } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import Loading from "components/common/Loading";

const isHHMM = (t) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(String(t || ""));

export default function OfficeEditPage() {
  const { id } = useParams();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [shiftLoading, setShiftLoading] = useState(true);
  const [shiftSaving, setShiftSaving] = useState(false);

  const [err, setErr] = useState("");
  const [shiftErr, setShiftErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    country: "India",
    lat: "",
    lng: "",
    radiusMeters: "",
    phone: "",
    workStart: "",
    workEnd: "",
    description: "",
    facilities: "",
    imageData: "",
  });

  const [shifts, setShifts] = useState([]);

  // New shifts (rows user is typing)
  const [newShifts, setNewShifts] = useState([
    { name: "", startTime: "", endTime: "", graceMins: 0 },
  ]);

  const [countryId, setCountryId] = useState(0);
  const [stateId, setStateId] = useState(0);

  const set = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  // ---------------- LOAD OFFICE + SHIFTS ----------------
  useEffect(() => {
    (async () => {
      setLoading(true);
      setShiftLoading(true);
      setErr("");
      setShiftErr("");

      try {
        const { data } = await getOffice(id);

        setForm({
          name: data?.name || "",
          address: data?.address || "",
          city: data?.city || "",
          state: data?.state || "",
          country: data?.country || "India",
          lat: data?.lat?.toString?.() || "",
          lng: data?.lng?.toString?.() || "",
          radiusMeters: (data?.radiusMeters ?? 50).toString(),
          phone: data?.phone || "",
          workStart: data?.workStart || "",
          workEnd: data?.workEnd || "",
          description: data?.description || "",
          facilities: Array.isArray(data?.facilities) ? data.facilities.join(", ") : "",
          imageData: data?.imageData || "",
        });

        const sRes = await getShiftsByOffice(id);

        // with your ok() helper, sRes.data should already be array
        const items = Array.isArray(sRes?.data)
          ? sRes.data
          : sRes?.data?.items || sRes?.data || [];

        setShifts(items);
      } catch (e) {
        setErr(e?.message || "Failed to load office.");
      } finally {
        setLoading(false);
        setShiftLoading(false);
      }
    })();
  }, [id]);

  // ---------------- MAP CALC ----------------
  const latN = Number(form.lat);
  const lngN = Number(form.lng);

  const hasCoords =
    Number.isFinite(latN) &&
    Number.isFinite(lngN) &&
    form.lat !== "" &&
    form.lng !== "";

  const mapSrc = useMemo(() => {
    if (!hasCoords) return "";
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lngN - 0.01
      }%2C${latN - 0.01}%2C${lngN + 0.01}%2C${latN + 0.01
      }&layer=mapnik&marker=${latN}%2C${lngN}`;
  }, [hasCoords, latN, lngN]);

  const onUseCurrentLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported.");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((p) => ({ ...p, lat: String(latitude), lng: String(longitude) }));
      },
      () => alert("Unable to fetch current location."),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onPickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return alert("Please select an image file.");
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, imageData: reader.result }));
    reader.readAsDataURL(file);
  };

  // ---------------- OFFICE SUBMIT ----------------
  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!form.name.trim() || !form.address.trim() || !form.city.trim() || !form.state.trim() || !form.country.trim()) {
      setErr("Please fill all required fields.");
      return;
    }

    if (!hasCoords) {
      setErr("Please provide valid latitude and longitude.");
      return;
    }

    const radiusN = Number(form.radiusMeters);

    const payload = {
      ...form,
      lat: latN,
      lng: lngN,
      radiusMeters: Number.isFinite(radiusN) && radiusN > 0 ? radiusN : 50,
      facilities: form.facilities
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    };

    try {
      await updateOffice(id, payload);
      nav(-1);
    } catch (e2) {
      setErr(e2?.message || "Failed to update office.");
    }
  };

  // ---------------- SHIFT HELPERS ----------------
  const validateShift = (s) => {
    if (!s.name?.trim()) return "Shift name required";
    if (!isHHMM(s.startTime)) return "Invalid start time";
    if (!isHHMM(s.endTime)) return "Invalid end time";
    const g = Number(s.graceMins ?? 0);
    if (!Number.isInteger(g) || g < 0 || g > 120)
      return "Grace minutes must be between 0 and 120";
    return null;
  };

  const updateExistingShiftField = (shiftId, key, value) => {
    setShifts((p) =>
      p.map((x) => (x._id === shiftId ? { ...x, [key]: value } : x))
    );
  };

  const saveExistingShift = async (s) => {
    setShiftErr("");
    const v = validateShift(s);
    if (v) return setShiftErr(v);

    setShiftSaving(true);
    try {
      const res = await updateShift(s._id, {
        name: s.name,
        startTime: s.startTime,
        endTime: s.endTime,
        graceMins: Number(s.graceMins ?? 0),   // ← add
      });

      // res.data is updated shift object (because ok() unwraps)
      if (res?.data?._id) {
        setShifts((p) => p.map((x) => (x._id === s._id ? res.data : x)));
      }
    } catch (e) {
      setShiftErr(e?.message || "Failed to update shift.");
    } finally {
      setShiftSaving(false);
    }
  };

  const removeExistingShift = async (shiftId) => {
    setShiftErr("");
    if (!window.confirm("Delete shift?")) return;

    setShiftSaving(true);
    try {
      await deleteShift(shiftId);
      setShifts((p) => p.filter((x) => x._id !== shiftId));
    } catch (e) {
      setShiftErr(e?.message || "Failed to delete shift.");
    } finally {
      setShiftSaving(false);
    }
  };

  // new shifts rows
  const addNewShiftRow = () => {
    setNewShifts((p) => [...p, { name: "", startTime: "", endTime: "", graceMins: 0 }]);
  };

  const removeNewShiftRow = (index) => {
    setNewShifts((p) => p.filter((_, idx) => idx !== index));
  };

  const updateNewShiftField = (index, key, value) => {
    setNewShifts((p) => p.map((x, idx) => (idx === index ? { ...x, [key]: value } : x)));
  };

  const saveNewShifts = async () => {
    setShiftErr("");

    // Keep rows user touched (name/time filled)
    const rows = newShifts
      .map((x) => ({
        name: x.name?.trim() || "",
        startTime: x.startTime || "",
        endTime: x.endTime || "",
        graceMins: Number(x.graceMins ?? 0),   // ← add
      }))
      .filter((x) => x.name || x.startTime || x.endTime);

    if (rows.length === 0) {
      setShiftErr("Add at least one shift row before saving.");
      return;
    }

    for (const r of rows) {
      const v = validateShift(r);
      if (v) {
        setShiftErr(v);
        return;
      }
    }

    setShiftSaving(true);
    try {
      const created = await Promise.all(
        rows.map((r) => createShift({ ...r, officeId: id }))
      );

      const createdItems = created
        .map((x) => x?.data)
        .filter(Boolean);

      setShifts((p) => [...createdItems, ...p]);
      setNewShifts([{ name: "", startTime: "", endTime: "" }]);
    } catch (e) {
      setShiftErr(e?.message || "Failed to create shifts.");
    } finally {
      setShiftSaving(false);
    }
  };

  // ---------------- RENDER ----------------
  if (loading) return <Loading fullscreen />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-navy-700"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h2 className="text-2xl font-semibold dark:text-white">Edit Office</h2>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
          {err && (
            <div className="bg-rose-50 text-rose-700 rounded-lg p-3 text-sm md:col-span-2">
              {err}
            </div>
          )}

          {/* Name */}
          <Field label="Name">
            <input
              name="name"
              value={form.name}
              onChange={set}
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
              required
            />
          </Field>

          {/* Country */}
          <Field label="Country">
            <CountrySelect
              placeHolder={form.country || "Select Country"}
              onChange={(val) => {
                setCountryId(val.id);
                setStateId(0);
                setForm((p) => ({
                  ...p,
                  country: val.name || "",
                  state: "",
                  city: "",
                }));
              }}
              containerClassName="w-full"
              inputClassName="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white dark:bg-navy-800"
            />
          </Field>

          {/* State */}
          <Field label="State">
            <StateSelect
              countryid={countryId}
              placeHolder={form.state || "Select State"}
              onChange={(val) => {
                setStateId(val.id);
                setForm((p) => ({
                  ...p,
                  state: val.name || "",
                  city: "",
                }));
              }}
              containerClassName="w-full dark:bg-navy-800"
              inputClassName="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white dark:bg-navy-800"
            />
          </Field>

          {/* City */}
          <Field label="City">
            <CitySelect
              countryid={countryId}
              stateid={stateId}
              placeHolder={form.city || "Select City"}
              onChange={(val) => {
                setForm((p) => ({
                  ...p,
                  city: val.name || "",
                }));
              }}
              containerClassName="w-full"
              inputClassName="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white dark:bg-navy-800"
            />
          </Field>

          {/* Address */}
          <Field label="Address" full>
            <input
              name="address"
              value={form.address}
              onChange={set}
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
              required
            />
          </Field>

          {/* Phone */}
          <Field label="Phone (optional)">
            <input
              name="phone"
              value={form.phone}
              onChange={set}
              placeholder="+91-XXXXXXXXXX"
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          {/* Work timings */}
          <Field label="Working In (Start)">
            <input
              type="time"
              name="workStart"
              value={form.workStart}
              onChange={set}
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <Field label="Working Out (End)">
            <input
              type="time"
              name="workEnd"
              value={form.workEnd}
              onChange={set}
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <Field label="Radius (meters)">
            <input
              name="radiusMeters"
              value={form.radiusMeters}
              onChange={set}
              placeholder="e.g., 200"
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          {/* Latitude + Use current */}
          <Field label="Latitude">
            <div className="flex items-center gap-2">
              <input
                name="lat"
                value={form.lat}
                onChange={set}
                placeholder="e.g., 25.6093"
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
                required
              />
              <button
                type="button"
                onClick={onUseCurrentLocation}
                className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-2 text-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                title="Use current location"
              >
                <LocateFixed className="h-4 w-4 dark:text-white" /> Use current
              </button>
            </div>
          </Field>

          {/* Longitude */}
          <Field label="Longitude">
            <input
              name="lng"
              value={form.lng}
              onChange={set}
              placeholder="e.g., 85.1234"
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
              required
            />
          </Field>

          {/* Map */}
          <div className="grid gap-3 md:col-span-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <MapPin className="h-4 w-4" />
              {hasCoords ? (
                <>
                  <span>
                    Previewing: {latN.toFixed(6)}, {lngN.toFixed(6)}
                  </span>
                  <a
                    href={`https://www.google.com/maps?q=${form.lat},${form.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 rounded px-2 py-0.5 font-medium text-brand-600 hover:underline dark:text-white"
                  >
                    Open in Google Maps
                  </a>
                </>
              ) : (
                <span>Enter latitude & longitude to preview</span>
              )}
            </div>

            <div className="overflow-hidden rounded-xl border dark:border-white/10 dark:bg-navy-700">
              {hasCoords ? (
                <iframe title="office-map" src={mapSrc} className="h-56 w-full" loading="lazy" />
              ) : (
                <div className="flex h-56 items-center justify-center text-sm text-gray-400">
                  Map preview
                </div>
              )}
            </div>
          </div>

          {/* ---------------- SHIFTS (FULL WIDTH) ---------------- */}
          <div className="md:col-span-2 border rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2 font-semibold">
              <Clock size={16} /> Shifts
            </div>

            {shiftErr && (
              <div className="text-sm text-red-600">{shiftErr}</div>
            )}

            {shiftLoading ? (
              <div>Loading shifts…</div>
            ) : (
              <div className="space-y-2">
                {shifts.map((s) => (
                  <div
                    key={s._id}
                    className="grid md:grid-cols-13 gap-2 items-end border p-3 rounded dark:border-white/10"
                  >
                    {/* Name */}
                    <div className="md:col-span-4">
                      <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">Name</label>
                      <input
                        value={s.name || ""}
                        onChange={(e) => updateExistingShiftField(s._id, "name", e.target.value)}
                        className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                      />
                    </div>

                    {/* Start */}
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">Start</label>
                      <input
                        type="time"
                        value={s.startTime || ""}
                        onChange={(e) => updateExistingShiftField(s._id, "startTime", e.target.value)}
                        className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                      />
                    </div>

                    {/* End */}
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">End</label>
                      <input
                        type="time"
                        value={s.endTime || ""}
                        onChange={(e) => updateExistingShiftField(s._id, "endTime", e.target.value)}
                        className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                      />
                    </div>

                    {/* Grace */}
                    <div className="md:col-span-3">
                      <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">
                        Grace (mins)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={120}
                        value={s.graceMins ?? 0}
                        placeholder="e.g. 15"
                        onChange={(e) =>
                          updateExistingShiftField(
                            s._id,
                            "graceMins",
                            e.target.value === "" ? "" : Math.min(120, Math.max(0, Number(e.target.value) || 0))
                          )
                        }
                        className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                      />
                    </div>

                    {/* Actions */}
                    <div className="md:col-span-2 flex gap-2 justify-end">
                      <button
                        type="button"
                        disabled={shiftSaving}
                        onClick={() => saveExistingShift(s)}
                        className="rounded border px-2 py-1.5 disabled:opacity-50 dark:border-white/10 dark:text-white"
                        title="Save"
                      >
                        <Save size={16} />
                      </button>
                      <button
                        type="button"
                        disabled={shiftSaving}
                        onClick={() => removeExistingShift(s._id)}
                        className="rounded border px-2 py-1.5 disabled:opacity-50 dark:border-white/10 dark:text-white"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                {shifts.length === 0 && (
                  <div className="text-sm text-gray-500">No shifts found for this office.</div>
                )}
              </div>
            )}

            {/* ADD NEW SHIFTS */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Add New Shifts</div>

                <button
                  type="button"
                  onClick={addNewShiftRow}
                  className="flex items-center gap-1 text-sm rounded-lg border px-3 py-2 hover:bg-gray-50"
                >
                  <Plus size={14} /> Add Row
                </button>
              </div>

              {newShifts.map((s, i) => (
                <div key={i} className="grid md:grid-cols-13 gap-2 items-end">
                  {/* Name */}
                  <div className="md:col-span-4">
                    <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">Name</label>
                    <input
                      placeholder="Morning / Evening / Night"
                      value={s.name}
                      onChange={(e) => updateNewShiftField(i, "name", e.target.value)}
                      className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                    />
                  </div>

                  {/* Start */}
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">Start</label>
                    <input
                      type="time"
                      value={s.startTime}
                      onChange={(e) => updateNewShiftField(i, "startTime", e.target.value)}
                      className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                    />
                  </div>

                  {/* End */}
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">End</label>
                    <input
                      type="time"
                      value={s.endTime}
                      onChange={(e) => updateNewShiftField(i, "endTime", e.target.value)}
                      className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                    />
                  </div>

                  {/* Grace */}
                  <div className="md:col-span-3">
                    <label className="mb-1 block text-xs text-gray-500 dark:text-white/60">
                      Grace (mins)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={120}
                      value={s.graceMins ?? 0}
                      placeholder="e.g. 15"
                      onChange={(e) =>
                        updateNewShiftField(
                          i,
                          "graceMins",
                          e.target.value === "" ? "" : Math.min(120, Math.max(0, Number(e.target.value) || 0))
                        )
                      }
                      className="w-full border px-2 py-1.5 rounded text-sm dark:bg-navy-800 dark:text-white dark:border-white/10"
                    />
                  </div>

                  {/* Remove row */}
                  <div className="md:col-span-2 flex items-end">
                    <button
                      type="button"
                      onClick={() => removeNewShiftRow(i)}
                      className="rounded border px-2 py-1.5 dark:border-white/10 dark:text-white"
                      title="Remove row"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                disabled={shiftSaving}
                onClick={saveNewShifts}
                className="flex items-center gap-1 text-sm rounded-lg border px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
              >
                <Save size={14} /> Save New Shifts
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <Field label="Description">
              <textarea
                name="description"
                rows={3}
                value={form.description}
                onChange={set}
                placeholder="Describe this office (parking, canteen, entry policy, etc.)"
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
              />
            </Field>
          </div>

          {/* Facilities */}
          <div className="md:col-span-2">
            <Field label="Facilities (comma-separated)">
              <input
                name="facilities"
                value={form.facilities}
                onChange={set}
                placeholder="Parking, Canteen, Meeting Rooms"
                className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-800 dark:text-white"
              />
            </Field>
          </div>

          {/* Image */}
          <div className="md:col-span-2">
            <Field label="Office Image">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onPickImage(e.target.files?.[0])}
                  className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-xs hover:file:bg-gray-50 dark:text-white dark:file:border-white/10 dark:file:bg-navy-700 dark:file:text-white dark:hover:file:bg-navy-600"
                />
                {form.imageData ? (
                  <img
                    src={form.imageData}
                    alt="Preview"
                    className="h-16 w-16 rounded-lg object-cover ring-1 ring-gray-200"
                  />
                ) : null}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                PNG/JPG, up to ~2MB recommended.
              </p>
            </Field>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={() => nav(-1)}
              className="hover:bg-black rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-white/80">
        {label}
      </label>
      {children}
    </div>
  );
}